import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth, apiClient } from '../auth/AuthContext';
import {
  buildAreaRows,
  buildUnmappedZoneRows,
  countUnmappedAreas,
  countUnmappedZones,
  getAvailableZonesForArea,
  normalizeAreas,
} from './bmsZonesUtils';
import AddZonePill from './AddZonePill';
import MappingView from './MappingView';
import type { AreaMappingRow, BuildingArea, Floor, FloorplanArea, MappingFilter, PageViewMode } from './types';

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 1000,
};

const modalCardStyle: React.CSSProperties = {
  background: 'var(--surface, #fff)',
  borderRadius: 12,
  padding: 24,
  width: '100%',
  maxWidth: 480,
  boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
};

const warningBannerStyle: React.CSSProperties = {
  backgroundColor: '#fefcbf',
  border: '1px solid #f6e05e',
  color: '#744210',
  borderRadius: 8,
  padding: 12,
  marginBottom: 16,
};

const editModeBannerStyle: React.CSSProperties = {
  backgroundColor: '#ebf8ff',
  border: '1px solid #90cdf4',
  color: '#2c5282',
  borderRadius: 8,
  padding: 12,
  marginBottom: 16,
};

const errorBannerStyle: React.CSSProperties = {
  backgroundColor: '#fed7d7',
  border: '1px solid #feb2b2',
  color: '#742a2a',
  borderRadius: 8,
  padding: 12,
  marginBottom: 16,
};

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'var(--surface-hover, #edf2f7)',
  border: '1px solid var(--border, #e2e8f0)',
  borderRadius: 999,
  padding: '2px 10px',
  fontSize: 13,
};

const filterButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: '6px 14px',
  borderRadius: 8,
  border: '1px solid var(--border, #e2e8f0)',
  background: active ? 'var(--primary, #3182ce)' : 'transparent',
  color: active ? '#fff' : 'var(--text, inherit)',
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
});

interface AreaModalProps {
  title: string;
  areaId: string;
  name: string;
  floorId: string;
  bmsZones: string[];
  floors: Floor[];
  availableAreaIds: string[];
  isEdit: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: (payload: { areaId: string; name: string; floorId: string; bmsZones: string[] }) => void;
}

const AreaModal: React.FC<AreaModalProps> = ({
  title,
  areaId,
  name,
  floorId,
  bmsZones,
  floors,
  availableAreaIds,
  isEdit,
  saving,
  onClose,
  onSubmit,
}) => {
  const [formAreaId, setFormAreaId] = useState(areaId);
  const [formName, setFormName] = useState(name);
  const [formFloorId, setFormFloorId] = useState(floorId);
  const [formZonesText, setFormZonesText] = useState(bmsZones.join(', '));

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const zoneList = formZonesText
      .split(/[,\n]/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    onSubmit({
      areaId: formAreaId.trim(),
      name: formName.trim(),
      floorId: formFloorId.trim(),
      bmsZones: zoneList,
    });
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Area ID (floorplan key)</label>
              {isEdit ? (
                <input className="ah-input" value={formAreaId} disabled />
              ) : availableAreaIds.length > 0 ? (
                <select
                  className="ah-input"
                  value={formAreaId}
                  onChange={(e) => setFormAreaId(e.target.value)}
                  required
                >
                  <option value="">Select area…</option>
                  {availableAreaIds.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="ah-input"
                  value={formAreaId}
                  onChange={(e) => setFormAreaId(e.target.value)}
                  placeholder="e.g. A"
                  required
                />
              )}
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Display name</label>
              <input
                className="ah-input"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Lobby East"
                required
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Floor</label>
              <select
                className="ah-input"
                value={formFloorId}
                onChange={(e) => setFormFloorId(e.target.value)}
                required
              >
                <option value="">Select floor…</option>
                {floors.map((floor) => (
                  <option key={floor.id} value={floor.id}>
                    {floor.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
                BACnet zones <span className="muted">(optional)</span>
              </label>
              <input
                className="ah-input"
                value={formZonesText}
                onChange={(e) => setFormZonesText(e.target.value)}
                placeholder="e.g. 0, 1, 2"
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="ah-btn ah-btn-outline" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="ah-btn" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AddZoneModalProps {
  areas: BuildingArea[];
  floors: Floor[];
  saving: boolean;
  onClose: () => void;
  onSubmit: (areaId: string, zoneId: string) => void;
}

const AddZoneModal: React.FC<AddZoneModalProps> = ({ areas, floors, saving, onClose, onSubmit }) => {
  const [areaId, setAreaId] = useState(areas[0]?.id ?? '');
  const [zoneId, setZoneId] = useState('');

  const floorNameById = useMemo(() => new Map(floors.map((floor) => [floor.id, floor.name])), [floors]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit(areaId, zoneId.trim());
  };

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalCardStyle} onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true">
        <h3 style={{ marginTop: 0 }}>Add BACnet Zone</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Assign a BACnet zone ID to an existing area.
        </p>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 14, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Area</label>
              <select className="ah-input" value={areaId} onChange={(e) => setAreaId(e.target.value)} required>
                {areas.length === 0 && <option value="">No areas available</option>}
                {areas.map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name} ({area.id}) — {floorNameById.get(area.floorId) ?? area.floorId}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>Zone ID</label>
              <input
                className="ah-input"
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
                placeholder="e.g. 5"
                required
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="ah-btn ah-btn-outline" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="ah-btn" disabled={saving || !areas.length}>
              {saving ? 'Saving…' : 'Add zone'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BmsZonesPage: React.FC = () => {
  const { user, token } = useAuth();
  const [dbAreas, setDbAreas] = useState<BuildingArea[]>([]);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [floorplanAreasByFloor, setFloorplanAreasByFloor] = useState<Record<string, FloorplanArea[]>>({});
  const [zoneInventory, setZoneInventory] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [filter, setFilter] = useState<MappingFilter>('all');
  const [viewMode, setViewMode] = useState<PageViewMode>('table');
  const [saving, setSaving] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const [showAddArea, setShowAddArea] = useState(false);
  const [addAreaPrefill, setAddAreaPrefill] = useState<{ areaId: string; name: string; floorId: string } | null>(null);
  const [showAddZone, setShowAddZone] = useState(false);
  const [editingRow, setEditingRow] = useState<AreaMappingRow | null>(null);

  const buildingId = user?.buildingId || '';
  const canLoad = Boolean(token && buildingId);

  const loadData = useCallback(async () => {
    if (!canLoad) return;

    setLoading(true);
    setError('');

    try {
      const [areasResponse, floorsResponse, zoneInventoryResponse] = await Promise.all([
        apiClient.get<BuildingArea[]>(`/zones?buildingId=${encodeURIComponent(buildingId)}`),
        apiClient.get<Floor[]>(`/buildings/${encodeURIComponent(buildingId)}/floors`),
        apiClient
          .get<{ zoneIds: string[] }>(`/buildings/${encodeURIComponent(buildingId)}/bms-zones`)
          .catch(() => ({ zoneIds: [] as string[] })),
      ]);

      const normalizedAreas = normalizeAreas(areasResponse);
      setDbAreas(normalizedAreas);
      setFloors(floorsResponse);
      setZoneInventory(zoneInventoryResponse.zoneIds ?? []);

      const floorplanEntries = await Promise.all(
        floorsResponse.map(async (floor) => {
          try {
            const detail = await apiClient.get<{ areas: FloorplanArea[] }>(
              `/buildings/${encodeURIComponent(buildingId)}/floors/${encodeURIComponent(floor.id)}`,
            );
            return [floor.id, detail.areas ?? []] as const;
          } catch {
            return [floor.id, []] as const;
          }
        }),
      );

      setFloorplanAreasByFloor(Object.fromEntries(floorplanEntries));
    } catch (err: any) {
      setError(err.message || 'Failed to load BMS zone mappings');
    } finally {
      setLoading(false);
    }
  }, [buildingId, canLoad]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshToken]);

  const areaRows = useMemo(
    () => buildAreaRows(floors, floorplanAreasByFloor, dbAreas),
    [floors, floorplanAreasByFloor, dbAreas],
  );

  const unmappedZoneRows = useMemo(
    () => buildUnmappedZoneRows(zoneInventory, dbAreas),
    [zoneInventory, dbAreas],
  );

  const unmappedAreaCount = useMemo(() => countUnmappedAreas(areaRows), [areaRows]);
  const unmappedZoneCount = useMemo(() => countUnmappedZones(zoneInventory, dbAreas), [zoneInventory, dbAreas]);

  const unmappedZoneIds = useMemo(
    () => new Set(unmappedZoneRows.map((row) => row.zoneId)),
    [unmappedZoneRows],
  );

  const getZonesForRow = useCallback(
    (row: AreaMappingRow) => getAvailableZonesForArea(row, zoneInventory, dbAreas),
    [zoneInventory, dbAreas],
  );

  const filteredAreaRows = useMemo(() => {
    if (filter === 'unmapped_areas') {
      return areaRows.filter((row) => row.isUnmappedArea);
    }
    if (filter === 'unmapped_zones') {
      return [];
    }
    return areaRows;
  }, [areaRows, filter]);

  const dbAreaIds = useMemo(() => new Set(dbAreas.map((area) => area.id)), [dbAreas]);

  const availableAreaIdsForAdd = useMemo(() => {
    const ids = new Set<string>();
    Object.entries(floorplanAreasByFloor).forEach(([, floorAreas]) => {
      floorAreas.forEach((area) => {
        if (!dbAreaIds.has(area.key)) {
          ids.add(area.key);
        }
      });
    });
    return Array.from(ids).sort();
  }, [floorplanAreasByFloor, dbAreaIds]);

  const refresh = () => setRefreshToken((prev) => prev + 1);

  const exitEditMode = () => {
    setIsEditMode(false);
    setShowAddArea(false);
    setAddAreaPrefill(null);
    setShowAddZone(false);
    setEditingRow(null);
  };

  const handleCreateArea = async (payload: { areaId: string; name: string; floorId: string; bmsZones: string[] }) => {
    try {
      setSaving(true);
      setError('');
      await apiClient.post('/zones', {
        id: payload.areaId,
        buildingId,
        name: payload.name,
        floorId: payload.floorId,
        bmsZones: payload.bmsZones,
      });
      setShowAddArea(false);
      setAddAreaPrefill(null);
      refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to create area');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateArea = async (payload: { areaId: string; name: string; floorId: string; bmsZones: string[] }) => {
    if (!editingRow?.dbRecord) return;

    try {
      setSaving(true);
      setError('');
      await apiClient.put(`/zones/${encodeURIComponent(editingRow.areaId)}`, {
        buildingId,
        name: payload.name,
        floorId: payload.floorId,
        bmsZones: payload.bmsZones,
      });
      setEditingRow(null);
      refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to update area');
    } finally {
      setSaving(false);
    }
  };

  const handleAddZone = async (areaId: string, zoneId: string, floorId?: string) => {
    const area = floorId
      ? dbAreas.find((entry) => entry.id === areaId && entry.floorId === floorId)
      : dbAreas.find((entry) => entry.id === areaId);
    const row = floorId
      ? areaRows.find((entry) => entry.areaId === areaId && entry.floorId === floorId)
      : areaRows.find((entry) => entry.areaId === areaId);

    if (area) {
      if (area.bmsZones.includes(zoneId)) {
        setError(`Zone "${zoneId}" is already mapped to ${area.name}.`);
        return;
      }

      try {
        setSaving(true);
        setError('');
        await apiClient.put(`/zones/${encodeURIComponent(areaId)}`, {
          buildingId,
          name: area.name,
          floorId: area.floorId,
          bmsZones: [...area.bmsZones, zoneId],
        });
        setShowAddZone(false);
        refresh();
      } catch (err: any) {
        setError(err.message || 'Failed to add zone');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!row) {
      setError('Selected area was not found.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await apiClient.post('/zones', {
        id: areaId,
        buildingId,
        name: row.name,
        floorId: row.floorId,
        bmsZones: [zoneId],
      });
      setShowAddZone(false);
      refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to add zone');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveZone = async (row: AreaMappingRow, zoneId: string) => {
    if (!isEditMode || !row.dbRecord) return;

    const confirmed = window.confirm(`Remove zone "${zoneId}" from ${row.name}?`);
    if (!confirmed) return;

    try {
      setSaving(true);
      setError('');
      await apiClient.put(`/zones/${encodeURIComponent(row.areaId)}`, {
        buildingId,
        name: row.dbRecord.name,
        floorId: row.dbRecord.floorId,
        bmsZones: row.dbRecord.bmsZones.filter((id) => id !== zoneId),
      });
      refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to remove zone');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArea = async (row: AreaMappingRow) => {
    if (!isEditMode || !row.dbRecord) return;

    const confirmed = window.confirm(`Delete area "${row.name}" (${row.areaId})? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setSaving(true);
      setError('');
      await apiClient.delete(`/zones/${encodeURIComponent(row.areaId)}?buildingId=${encodeURIComponent(buildingId)}`);
      refresh();
    } catch (err: any) {
      setError(err.message || 'Failed to delete area');
    } finally {
      setSaving(false);
    }
  };

  if (!buildingId) {
    return (
      <div className="ah-shell">
        <div className="ah-panel">
          <div className="ah-card">
            <h1 className="ah-title">BMS Zones</h1>
            <p className="muted">Sign in with a building-assigned account to manage zones.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ah-shell">
      <div className="ah-panel">
        <div className="ah-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <div>
              <h1 className="ah-title" style={{ marginBottom: 4 }}>
                BMS Zones
              </h1>
              <p className="muted" style={{ margin: 0 }}>
                Manage area-to-BACnet zone mappings for building {buildingId}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button className="ah-btn ah-btn-outline" onClick={refresh} disabled={loading || saving}>
                Refresh
              </button>
              <button
                className={`ah-btn ${isEditMode ? 'ah-btn-outline' : ''}`}
                onClick={() => {
                  if (isEditMode) {
                    exitEditMode();
                  } else {
                    setIsEditMode(true);
                  }
                }}
                disabled={loading || saving}
              >
                {isEditMode ? 'Done editing' : 'Edit mode'}
              </button>
              {isEditMode && (
                <>
                  <button className="ah-btn" onClick={() => { setAddAreaPrefill(null); setShowAddArea(true); }} disabled={loading || saving}>
                    Add area
                  </button>
                  <button
                    className="ah-btn ah-btn-outline"
                    onClick={() => setShowAddZone(true)}
                    disabled={loading || saving || dbAreas.length === 0}
                  >
                    Add zone
                  </button>
                </>
              )}
            </div>
          </div>

          {isEditMode && (
            <div style={editModeBannerStyle}>
              Edit mode is active. You can create areas, assign BACnet zones, and update existing mappings.
            </div>
          )}

          {(unmappedAreaCount > 0 || unmappedZoneCount > 0) && (
            <div style={warningBannerStyle}>
              {unmappedAreaCount > 0 && (
                <div>
                  {unmappedAreaCount} area{unmappedAreaCount === 1 ? '' : 's'} {unmappedAreaCount === 1 ? 'has' : 'have'} no
                  BACnet zone mapping. After-hours requests for these areas may not control the BMS correctly.
                </div>
              )}
              {unmappedZoneCount > 0 && (
                <div style={{ marginTop: unmappedAreaCount > 0 ? 6 : 0 }}>
                  {unmappedZoneCount} BACnet zone{unmappedZoneCount === 1 ? '' : 's'}{' '}
                  {unmappedZoneCount === 1 ? 'is' : 'are'} not assigned to any area.
                </div>
              )}
            </div>
          )}

          {error && <div style={errorBannerStyle}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(['table', 'mapping'] as PageViewMode[]).map((value) => (
                <button
                  key={value}
                  type="button"
                  style={filterButtonStyle(viewMode === value)}
                  onClick={() => setViewMode(value)}
                >
                  {value === 'table' ? 'Table' : 'Mapping'}
                </button>
              ))}
            </div>
            <div style={{ width: 1, height: 24, background: 'var(--border, #e2e8f0)', margin: '0 4px' }} />
            {(['all', 'unmapped_areas', 'unmapped_zones'] as MappingFilter[]).map((value) => (
              <button
                key={value}
                type="button"
                style={filterButtonStyle(filter === value)}
                onClick={() => setFilter(value)}
              >
                {value === 'all' ? 'All' : value === 'unmapped_areas' ? 'Unmapped areas' : 'Unmapped zones'}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>Loading mappings…</div>
          ) : viewMode === 'mapping' ? (
            <MappingView
              areaRows={areaRows}
              zoneInventory={zoneInventory}
              unmappedZoneIds={unmappedZoneIds}
              filter={filter}
              isEditMode={isEditMode}
              saving={saving}
              onAddZone={handleAddZone}
              getAvailableZonesForArea={getZonesForRow}
            />
          ) : filter === 'unmapped_zones' ? (
            <div className="ah-table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>BACnet Zone ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unmappedZoneRows.length === 0 ? (
                    <tr>
                      <td colSpan={2} style={{ textAlign: 'center', padding: 24 }}>
                        All BACnet zones are assigned to areas.
                      </td>
                    </tr>
                  ) : (
                    unmappedZoneRows.map((row) => (
                      <tr key={row.zoneId}>
                        <td style={{ fontWeight: 600 }}>{row.zoneId}</td>
                        <td>
                          <span style={{ ...chipStyle, background: '#fefcbf', borderColor: '#f6e05e' }}>Unassigned</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="ah-table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Area</th>
                    <th>Floor</th>
                    <th>Mapped zones</th>
                    <th>Status</th>
                    <th>Updated</th>
                    {isEditMode && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredAreaRows.length === 0 ? (
                    <tr>
                      <td colSpan={isEditMode ? 6 : 5} style={{ textAlign: 'center', padding: 24 }}>
                        {filter === 'unmapped_areas' ? 'No unmapped areas found.' : 'No area mappings found.'}
                      </td>
                    </tr>
                  ) : (
                    filteredAreaRows.map((row) => (
                      <tr key={row.rowKey}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{row.name}</div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {row.areaId}
                          </div>
                        </td>
                        <td>{row.floorName}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                            {row.bmsZones.map((zoneId) => (
                              <span key={zoneId} style={chipStyle}>
                                {zoneId}
                                {isEditMode && row.dbRecord && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveZone(row, zoneId)}
                                    disabled={saving}
                                    style={{
                                      border: 'none',
                                      background: 'transparent',
                                      cursor: 'pointer',
                                      padding: 0,
                                      lineHeight: 1,
                                      color: '#e53e3e',
                                    }}
                                    aria-label={`Remove zone ${zoneId}`}
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            ))}
                            {isEditMode && (
                              <AddZonePill
                                areaId={row.areaId}
                                floorId={row.floorId}
                                availableZoneIds={getZonesForRow(row)}
                                saving={saving}
                                onAdd={handleAddZone}
                                chipStyle={chipStyle}
                              />
                            )}
                            {row.bmsZones.length === 0 && !isEditMode && (
                              <span className="muted">—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {row.isUnmappedArea ? (
                            <span style={{ ...chipStyle, background: '#fefcbf', borderColor: '#f6e05e' }}>Unmapped</span>
                          ) : (
                            <span style={{ ...chipStyle, background: '#c6f6d5', borderColor: '#9ae6b4' }}>Mapped</span>
                          )}
                        </td>
                        <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</td>
                        {isEditMode && (
                          <td>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {row.dbRecord ? (
                                <>
                                  <button
                                    className="ah-btn ah-btn-outline"
                                    onClick={() => setEditingRow(row)}
                                    disabled={saving}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="ah-btn ah-btn-danger"
                                    onClick={() => handleDeleteArea(row)}
                                    disabled={saving}
                                  >
                                    Delete
                                  </button>
                                </>
                              ) : (
                                <button
                                  className="ah-btn"
                                  onClick={() => {
                                    setAddAreaPrefill({
                                      areaId: row.areaId,
                                      name: row.name,
                                      floorId: row.floorId,
                                    });
                                    setShowAddArea(true);
                                  }}
                                  disabled={saving}
                                >
                                  Map area
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showAddArea && (
        <AreaModal
          key={`add-${addAreaPrefill?.areaId ?? 'new'}`}
          title="Add area"
          areaId={addAreaPrefill?.areaId ?? ''}
          name={addAreaPrefill?.name ?? ''}
          floorId={addAreaPrefill?.floorId ?? floors[0]?.id ?? ''}
          bmsZones={[]}
          floors={floors}
          availableAreaIds={
            addAreaPrefill?.areaId && !dbAreaIds.has(addAreaPrefill.areaId)
              ? [addAreaPrefill.areaId, ...availableAreaIdsForAdd.filter((id) => id !== addAreaPrefill.areaId)]
              : availableAreaIdsForAdd
          }
          isEdit={false}
          saving={saving}
          onClose={() => {
            setShowAddArea(false);
            setAddAreaPrefill(null);
          }}
          onSubmit={handleCreateArea}
        />
      )}

      {editingRow?.dbRecord && (
        <AreaModal
          title={`Edit area — ${editingRow.name}`}
          areaId={editingRow.areaId}
          name={editingRow.dbRecord.name}
          floorId={editingRow.dbRecord.floorId}
          bmsZones={editingRow.dbRecord.bmsZones}
          floors={floors}
          availableAreaIds={[]}
          isEdit
          saving={saving}
          onClose={() => setEditingRow(null)}
          onSubmit={handleUpdateArea}
        />
      )}

      {showAddZone && (
        <AddZoneModal
          areas={dbAreas}
          floors={floors}
          saving={saving}
          onClose={() => setShowAddZone(false)}
          onSubmit={handleAddZone}
        />
      )}
    </div>
  );
};

export default BmsZonesPage;
